import { ajv, JSONSchema7WithErrors } from '@cibo/core'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { AutoFormProps } from 'uniforms'
import JSONSchemaBridge from 'uniforms-bridge-json-schema'
import { AutoForm } from 'uniforms-mui'
import { AutoFields } from './AutoFields'
import { CiboAutoFormContext } from './CiboAutoFormContext'
import { dereferenceSchema } from './dereferenceSchema'
import { buildRequiredFieldsLookup } from './requiredFields/collectRequiredFields'

function createValidator(schema: JSONSchema7WithErrors) {
  const validator = ajv.compile(schema)

  return (model: any) => {
    validator(model)
    return validator.errors?.length ? { details: validator.errors } : null
  }
}

// Normal validator helper
function createWarningsValidator(schema: JSONSchema7WithErrors) {
  const validate = ajv.compile(schema)
  return (model: any) => {
    const errors: Record<string, string> = {}
    validate(model)
    if (validate.errors) {
      for (const err of validate.errors) {
        const path = err.instancePath.slice(1) // remove leading "/"
        errors[path] = err.message || 'Error'
      }
    }
    return errors
  }
}

type CiboAutoFormProps = Partial<Omit<AutoFormProps<any>, 'schema'>> & {
  warningSchema?: any
  renderDisplay?: (config: { field: any; itemProps: any }) => ReactNode
  schema: JSONSchema7WithErrors
}

export function CiboAutoForm({
  model,
  schema,
  warningSchema,
  renderDisplay,
  ...props
}: CiboAutoFormProps) {
  const [warnings, setWarnings] = useState<Record<string, string>>({})

  const warningValidator = useMemo(() => {
    return warningSchema && createWarningsValidator(warningSchema)
  }, [warningSchema])

  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const touch = useCallback((name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }))
  }, [])

  const bridge = useMemo<JSONSchemaBridge | undefined>(() => {
    if (!schema?.type) {
      console.error(
        "The configured schema does not have a 'type' property.\n\n",
        JSON.stringify(schema, null, 2),
        '\n\n'
      )
      throw new Error("Schema must include a 'type' property.")
    }

    const formSchema: JSONSchema7WithErrors = {
      type: 'object',
      properties: { value: { $id: 'schema', ...dereferenceSchema(schema, schema) } },
      required: ['value'],
    }

    return new JSONSchemaBridge({
      schema: formSchema,
      validator: createValidator(formSchema),
    })
  }, [schema])

  const requiredFields = useMemo(() => {
    // Handle both raw schema and JSONSchemaBridge
    const rawSchema = (schema as any)?.schema || schema

    return buildRequiredFieldsLookup(rawSchema, model)
  }, [schema, model])

  const isFieldRequired = useCallback((name: string) => requiredFields.get(name) ?? false, [
    requiredFields,
  ])

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <CiboAutoFormContext.Provider
        value={{ renderDisplay, warnings, touch, touched, isFieldRequired }}
      >
        <AutoForm
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          schema={bridge!}
          model={model}
          {...props}
          onChangeModel={(newModel: any) => {
            const warningResults = warningValidator?.(newModel)
            setWarnings(warningResults)

            // @ts-ignore This has been working but type seems wrong. Need to check.
            props.onChangeModel?.(newModel)
          }}
        >
          {props.children ?? <AutoFields />}
        </AutoForm>
      </CiboAutoFormContext.Provider>
    </LocalizationProvider>
  )
}
